import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../supabaseClient';
import { queryKeys } from '../../lib/query/queryKeys';
import { STALE } from '../../lib/query/staleTimes';
import { getFindLiveUntil } from '../../utils/eventCutoff';
import { getEventStartMs, pickCurrentLiveEvent } from '../../lib/proximity/data/eventStart';
import { useMyRsvps } from './useMyRsvps';

/** Same lead-in as FindProximityContext's isEventLiveNow: a session may start 12 h before the (first) date. */
// 3 h before (migration 147; was 12 h). Mirrors FindProximityContext.
const LIVE_BEFORE_MS = 3 * 3600 * 1000;
const HOUR_MS = 3600 * 1000;

/** Whether an event row (event_date/event_datetime, ends_at, is_festival, multi_day, status) is on now for Find. */
export function isRsvpEventLiveNow(event, now = Date.now()) {
  if (!event || event.status === 'cancelled') return false;
  // Date-only: midnight in the event's zone, not UTC midnight (the afternoon before; 2026-10-16 audit).
  const start = getEventStartMs(event);
  if (!Number.isFinite(start)) return false;
  const until = getFindLiveUntil(event);
  return now >= start - LIVE_BEFORE_MS && (until == null || now < until.getTime());
}

/**
 * The event you are going to that is on right now, group or not (Sam, 2026-10-04: "regardless of if they're in
 * a group or not, we should start tracking them"). Drives the solo Find session: your position is published for
 * the day you have, so the map works for you alone and a group you join later sees you at once. Null when no
 * going RSVP is live. One small query over the going RSVPs, narrowed to events whose end is near or ahead.
 */
export function useLiveRsvpEvent(userId, { authReady = true } = {}) {
  const { data: rsvps, isLoading: rsvpsLoading } = useMyRsvps(userId, {
    enabled: Boolean(userId) && authReady,
  });
  const goingIds = useMemo(
    () =>
      (rsvps ?? [])
        .filter((r) => r.status === 'going')
        .map((r) => r.event_id)
        .sort(),
    [rsvps],
  );
  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: [...queryKeys.rsvps.mine(userId), 'live-events', goingIds],
    enabled: Boolean(userId) && authReady && goingIds.length > 0,
    staleTime: STALE.user,
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * HOUR_MS).toISOString();
      const { data, error } = await supabase
        .from('events')
        .select(
          'event_id, event_name, display_name, display_name_override, event_date, event_time, event_datetime, timezone, ends_at, is_festival, multi_day, status, festival_parent_event_id, venues(latitude, longitude, timezone)',
        )
        .in('event_id', goingIds)
        // A row without ends_at (inserted outside the 134 trigger) still counts; the client rule fills the gap.
        .or(`ends_at.gte.${since},ends_at.is.null`);
      if (error) throw error;
      return data ?? [];
    },
  });
  // Re-evaluated every minute: the window opens and closes with the clock, not with a refetch (the query's rows
  // are structurally shared across identical refetches, so the array alone never changes).
  const [minute, setMinute] = useState(() => Math.floor(Date.now() / 60_000));
  useEffect(() => {
    const t = setInterval(() => setMinute(Math.floor(Date.now() / 60_000)), 15_000);
    return () => clearInterval(t);
  }, []);
  const event = useMemo(() => {
    const now = Date.now();
    const live = (events ?? []).filter((e) => isRsvpEventLiveNow(e, now));
    // Two live events at once: the one whose start most recently passed, not the earliest - which kept a day party's
    // session all through the night show (2026-10-16 audit). Ties go to the id, so the key does not flip.
    return pickCurrentLiveEvent(live, {
      startMs: getEventStartMs,
      id: (e) => String(e.event_id),
      now,
    });
  }, [events, minute]); // eslint-disable-line react-hooks/exhaustive-deps -- minute is the clock
  // Nothing on yet: the show whose window opens next, so Find can say when it turns on (Sam's pick C, 2026-11-01).
  const next = useMemo(() => {
    if (event) return null;
    const now = Date.now();
    let best = null;
    for (const e of events ?? []) {
      if (e.status === 'cancelled') continue;
      const opensAt = getEventStartMs(e) - LIVE_BEFORE_MS;
      if (!(opensAt > now) || (best && best.opensAt <= opensAt)) continue;
      best = { event: e, opensAt };
    }
    return best;
  }, [event, events, minute]); // eslint-disable-line react-hooks/exhaustive-deps -- minute is the clock
  // Still finding out: Find must not say "No shows" to someone going for the second this takes (2026-10-12 audit).
  return { event, next, loading: Boolean(rsvpsLoading || (goingIds.length > 0 && eventsLoading)) };
}
